// src/components/VisitStats.js
import React, { useState, useEffect, useRef } from 'react';
import { Eye, Users, TrendingUp } from 'lucide-react';
import visitTracker from '../utils/visitTracker';
import './VisitStats.css';

const VisitStats = ({ 
  pageName, 
  showDetailed = false, 
  className = '',
  user = null 
}) => {
  const [stats, setStats] = useState({
    visit_count: 0,
    unique_visitors: 0,
    loading: true,
    error: null
  });

  const [detailedStats, setDetailedStats] = useState(null);
  const hasRecorded = useRef(false); // 방문 기록 중복 방지용

  useEffect(() => {
    let isMounted = true;
    
    const recordVisitAndLoadStats = async () => {
      // 이미 기록했거나 컴포넌트가 언마운트된 경우 건너뛰기
      if (hasRecorded.current || !isMounted) {
        return;
      }

      hasRecorded.current = true;

      try {
        // 페이지 방문 기록 - 이메일을 우선적으로 사용
        const userId = user?.email || user?.uid || null;
        await visitTracker.recordVisit(pageName, userId);

        // 초기 통계 로드
        if (isMounted) {
          await loadStats();
        }
      } catch (error) {
        console.error('방문 기록 및 통계 로드 오류:', error);
      }
    };

    const loadStats = async () => {
      if (!isMounted) return;

      try {
        const result = await visitTracker.getPageStats(pageName);
        
        if (result.success && isMounted) {
          setStats({
            ...result.stats,
            loading: false,
            error: null
          });

          // 상세 통계도 로드 (showDetailed가 true인 경우)
          if (showDetailed) {
            const detailedResult = await visitTracker.getPageStatsInPeriod(pageName, 7);
            if (detailedResult.success && isMounted) {
              setDetailedStats(detailedResult);
            }
          }
        } else if (isMounted) {
          setStats(prev => ({
            ...prev,
            loading: false,
            error: result.error
          }));
        }
      } catch (error) {
        console.error('통계 로드 오류:', error);
        if (isMounted) {
          setStats(prev => ({
            ...prev,
            loading: false,
            error: '통계를 불러올 수 없습니다.'
          }));
        }
      }
    };

    // 초기 방문 기록 및 통계 로드
    recordVisitAndLoadStats();

    // 주기적으로 통계 업데이트 (30초마다)
    const interval = setInterval(() => {
      if (isMounted) {
        loadStats();
      }
    }, 30000);

    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, [pageName, user, showDetailed]);

  // 컴포넌트가 다시 마운트될 때 방문 기록 리셋
  useEffect(() => {
    return () => {
      hasRecorded.current = false;
    };
  }, []);

  const formatNumber = (num) => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toLocaleString();
  };

  if (stats.loading) {
    return (
      <div className={`visit-stats loading ${className}`}>
        <div className="stats-spinner"></div>
        <span>통계 로드 중...</span>
      </div>
    );
  }

  if (stats.error) {
    return (
      <div className={`visit-stats error ${className}`}>
        <span>⚠️ {stats.error}</span>
      </div>
    );
  }

  return (
    <div className={`visit-stats ${className}`}>
      {/* 기본 통계 */} 
        <div className="stat-item">
          <Eye className="stat-icon" />
          <div className="stat-content"> 
            <span className="stat-label">총 방문 수: {formatNumber(stats.visit_count)}</span> {/* 레이블 변경 제안 */}
          </div> 
          <div className="stat-content"> 
            <span className="stat-label">고유 방문자: {formatNumber(stats.unique_visitors)}</span> {/* 서버 수정 후에는 전체 고유 방문자 */}
          </div>
        </div> 

      {/* 마지막 업데이트 시간 */}
      {/* <div className="stats-footer">
        <span className="last-updated">
          마지막 업데이트: {new Date(stats.updated_at).toLocaleTimeString('ko-KR')}
        </span>
      </div> */}
    </div>
  );
};

export default VisitStats;